// @ts-check

/**
 * @typedef {Object} MetaData
 * @property {string=} title
 * @property {string=} titleSlug
 * @property {string=} author
 * @property {string|string[]=} genre
 * @property {string[]=} categories
 * @property {string=} language      // e.g., 'fi', 'sv-SE'
 * @property {string=} wikipedia
 * @property {string[]=} sameAs      // extra identifiers (Gutenberg, IMSLP, Wikidata)
 * @property {string=} cover
 * @property {string=} query
 * @property {string=} isbn
 * @property {string=} series
 */

/**
 * @typedef {Object} Options
 * @property {string=} url
 * @property {string=} image         // site-relative path preferred
 * @property {string=} variant       // 'concise' | 'bold' | 'curiosity' (falls back if unknown)
 * @property {string=} updatedTime   // ISO
 * @property {string=} publishedTime // ISO
 * @property {string[]=} seeAlso     // optional related URLs for og:see_also
 */

/**
 * @typedef {Object} Bundle
 * @property {string} htmlLang
 * @property {'ltr'|'rtl'} dir
 * @property {string} title
 * @property {string} description
 * @property {string} keywords            // comma string for <meta name="keywords">
 * @property {Object} og
 * @property {Object} twitter
 * @property {Object} structuredData      // JSON-LD object (includes keywords as array)
 * @property {string} robots
 * @property {string} canonical
 * @property {{prev?:string,next?:string,first?:string,last?:string}=} pagination
 * @property {{hrefLang:string,href:string}[]=} hreflangAlternates
 * @property {string[]=} ogLocaleAlternates
 */

import {
  BASE_URL,
  DEFAULT_CURRENCY,
  RTL_LANGS,
  OG_LOCALE as ogLocale,
  TWITTER_HANDLE,
  PLACEHOLDER_IMAGE,
  OG_IMAGE_WIDTH,
  OG_IMAGE_HEIGHT,
  TITLE_CLAMP,
  DESCRIPTION_CLAMP,
  ROBOTS_INDEX,
  ROBOTS_NOINDEX
} from './constants.js';

const capitalize = str => str?.charAt(0)?.toUpperCase() + str?.slice(1) || '';

const EN_SMALL = new Set(['and','or','the','a','an','of','in','on','for','to','at','by','from']);
const capitalizeAllWords = str => {
  if (typeof str !== 'string') return '';
  const parts = str.split(' ');
  return parts.map((w,i) => {
    const low = w.toLowerCase();
    if (i>0 && i<parts.length-1 && EN_SMALL.has(low)) return low;
    return w.charAt(0).toUpperCase() + w.slice(1);
  }).join(' ');
};

const stripHtml = (str) =>
  typeof str === 'string'
    ? str
        .replace(/<[^>]*>?/gm, '')
        .replace(/&nbsp;/gi, ' ')
        .replace(/\s+/g, ' ')
        .replace(/&(amp|#38);/gi, '&')
        .replace(/&(quot|#34);/gi, '"')
        .replace(/&(#39|apos);/gi, "'")
        .replace(/&(lt|#60);/gi, '<')
        .replace(/&(gt|#62);/gi, '>')
        .trim()
    : '';

// 60/155 chars ≈ ~580px/~960px on SERP. Prefer ending near punctuation to avoid mid-word ellipses.
const clamp = (s='', n=155) => {
  if (s.length <= n) return s;
  const cut = s.slice(0, n);
  const tail = cut.slice(Math.max(0, n - 12));
  const p = Math.max(tail.lastIndexOf('.'), tail.lastIndexOf(','), tail.lastIndexOf(':'), tail.lastIndexOf(';'), tail.lastIndexOf('—'));
  if (p !== -1) return cut.slice(0, n - 12 + p + 1).trim() + '…';
  const space = cut.lastIndexOf(' ');
  return (space > 0 ? cut.slice(0, space) : cut).trim() + '…';
};

const absolutize = (u) => {
  if (!u) return u;
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  if (u.startsWith('//')) return `https:${u}`;
  // keep images absolute everywhere; pass site-relative paths to options.image to preserve this guarantee.
  const joined = `${BASE_URL}${u.startsWith('/') ? '' : '/'}${u}`;
  return joined.replace(/([^:]\/)\/+/g, '$1'); // normalize double slashes
};

const sanitizeUrl = (u) => {
  if (!u) return '';
  const s = String(u).trim();
  if (/^(javascript|data):/i.test(s)) return '';
  return s;
};

// Stronger sanitizer
const clean = (v) => stripHtml(String(v ?? '')).replace(/\s+/g, ' ').trim();

const slugify = (s='') => {
  let t = String(s);
  t = t.normalize('NFKD').replace(/[\u0300-\u036f]/g, ''); // strip diacritics
  t = t
    .replace(/ß/g,'ss').replace(/æ/g,'ae').replace(/œ/g,'oe').replace(/ø/g,'o').replace(/đ/g,'d')
    .replace(/[åä]/g,'a').replace(/ö/g,'o').replace(/ñ/g,'n').replace(/ł/g,'l');
  return t.toLowerCase().trim().replace(/[^a-z0-9]+/g,'-').replace(/(^-|-$)/g,'');
};

const buildFallbackUrl = (type, data) => {
  if (type === 'book' && data.titleSlug) return `${BASE_URL}/book/${data.titleSlug}`;
  if (type === 'author' && data.author)  return `${BASE_URL}/author/${encodeURIComponent(slugify(data.author))}`;
  if (type === 'genre' && data.genre) {
    const genreName = typeof data.genre === 'string'
      ? data.genre
      : (data.genre?.genre || data.genre?.name || data.genre?.label || '');
    return `${BASE_URL}/genre/${encodeURIComponent(slugify(genreName))}`;
  }
  if (type === 'search' && data.query)   return `${BASE_URL}/search?query=${encodeURIComponent(String(data.query))}`;
  return `${BASE_URL}/`;
};

const quoteSmart = (s='') => /["“”«»]/.test(s) ? s : `"${s}"`;

const STOP_MAP = Object.freeze({
  en: new Set(['unknown','untitled','this genre','this author','your search']),
  sv: new Set(['okänd','namnlös','denna genre','denna författare','din sökning']),
  es: new Set(['desconocido','sin título','este género','este autor','tu búsqueda']),
  fi: new Set(['tuntematon','nimetön','tämä genre','tämä kirjailija','hakusi']),
  de: new Set(['unbekannt','ohne titel','dieses genre','dieser autor','ihre suche']),
  fr: new Set(['inconnu','sans titre','ce genre','cet auteur','votre recherche']),
  pt: new Set(['desconhecido','sem título','este gênero','este autor','sua pesquisa']),
  ru: new Set(['неизвестен','без названия','этот жанр','этот автор','ваш запрос']),
  zh: new Set(['未知','无标题','此类型','此作者','您的搜索']),
  pl: new Set(['nieznany','bez tytułu','ten gatunek','ten autor','twoje wyszukiwanie']),
  it: new Set(['sconosciuto','senza titolo','questo genere','questo autore','la tua ricerca']),
  da: new Set(['ukendt','uden titel','denne genre','denne forfatter','din søgning']),
});

const generateDynamicKeywords = (data, type, lang='en') => {
  const raw = [
    ...(data.title ? [capitalizeAllWords(stripHtml(data.title))] : []),
    ...(data.titleSlug ? [data.titleSlug] : []),
    ...(data.author ? [capitalizeAllWords(stripHtml(data.author))] : []),
    ...(Array.isArray(data.categories)
      ? [...new Set(data.categories.map(c => {
          const v = typeof c === 'string' ? c : (c?.name || c?.label || '');
          return capitalizeAllWords(stripHtml(v));
        }).filter(Boolean))]
      : []),
    ...(data.language ? [`${capitalizeAllWords(stripHtml(data.language))} ebooks`] : []),
    'public domain',
    'HermesReads',
    ...(type === 'book' ? ['classic literature','free books','ebooks']
      : type === 'genre' ? ['classic books','literature','philosophy']
      : type === 'author' ? ['author works','public domain books','free reads']
      : ['search results','book archive','free literature'])
  ];

  const seen = new Set();
  const cleaned = [];
  for (const k of raw) {
    const v = String(k || '').trim();
    const low = v.toLowerCase();
    const stop = STOP_MAP[lang?.split('-')[0]] || STOP_MAP.en;
    if (!v || stop.has(low) || seen.has(low)) continue;
    seen.add(low);
    cleaned.push(v);
  }
  return cleaned.slice(0, 10).join(', ');
};
	
const templates = {
  en: {
    friendlyType: { book: 'Book', genre: 'Genre', author: 'Author', search: 'Search' },
    title: {
      book: ({ title = 'Untitled', author = 'Unknown' }) => `${quoteSmart(capitalize(stripHtml(title)))} by ${capitalizeAllWords(stripHtml(author))} | HermesReads`,
      genre: (genre = 'Unknown Genre') => {
		  const genreName = typeof genre === 'string' ? genre : genre?.genre || 'Unknown Genre';
		  return `Browse essential ${capitalizeAllWords(genreName)} books — from iconic classics to hidden gems | HermesReads`;
		},
      author: (author = 'Unknown') => `${capitalizeAllWords(stripHtml(author))} | Author on HermesReads`,
      search: (query = 'your search') => `Search ${quoteSmart(stripHtml(query))} | HermesReads`
    },
    description: {
      book: ({ title = 'Untitled', author = 'Unknown' }) => `Read ${quoteSmart(capitalize(stripHtml(title)))} by ${capitalizeAllWords(stripHtml(author))} for free — explore timeless classics on HermesReads.`,
      genre: (genre = 'this genre') => `Browse essential ${capitalizeAllWords(genre)} books — from iconic classics to hidden gems.`,
      author: (author = 'this author') => `Explore free public domain works by ${capitalizeAllWords(stripHtml(author))} on HermesReads.`,
      search: (query = 'your search') => `Find ${quoteSmart(stripHtml(query))} in the HermesReads archive of free books.`
    },
    variants: {
      book: {
        concise: ({ title = 'Untitled', author = 'Unknown' }) => `Read ${quoteSmart(capitalize(stripHtml(title)))} Free | HermesReads`,
        bold: ({ title = 'Untitled', author = 'Unknown' }) => `Unlock ${quoteSmart(capitalize(stripHtml(title)))} by ${capitalizeAllWords(stripHtml(author))} Now! | HermesReads`,
        curiosity: ({ title = 'Untitled', author = 'Unknown' }) => `Why Read ${quoteSmart(capitalize(stripHtml(title)))} by ${capitalizeAllWords(stripHtml(author))}? | HermesReads`
      },
      genre: {
        concise: (genre = 'this genre') => `${capitalizeAllWords(genre)} Books | HermesReads`,
        bold: (genre = 'this genre') => `Discover ${capitalizeAllWords(genre)} Classics! | HermesReads`,
        curiosity: (genre = 'this genre') => `What Makes ${capitalizeAllWords(genre)} Special? | HermesReads`
      },
      author: {
        concise: (author = 'Unknown') => `${capitalizeAllWords(stripHtml(author))} Books | HermesReads`,
        bold: (author = 'Unknown') => `Explore ${capitalizeAllWords(stripHtml(author))} Now! | HermesReads`,
        curiosity: (author = 'Unknown') => `Who Was ${capitalizeAllWords(stripHtml(author))}? | HermesReads`
      },
      search: {
        concise: (query = 'your search') => `Search ${quoteSmart(stripHtml(query))} | HermesReads`,
        bold: (query = 'your search') => `Find ${quoteSmart(stripHtml(query))} Now! | HermesReads`,
        curiosity: (query = 'your search') => `What's Behind ${quoteSmart(stripHtml(query))}? | HermesReads`
      }
    }
  },
  de: {
    friendlyType: {
      book: 'Buch',
      genre: 'Genre',
      author: 'Autor',
      search: 'Suche'
    },
    title: {
      book: ({ title = 'Unbenannt', author = 'Unbekannt' }) => `${quoteSmart(capitalize(stripHtml(title)))} von ${capitalizeAllWords(stripHtml(author))} | HermesReads`,
      genre: (genre = 'Unbekanntes Genre') => `Entdecke ${capitalizeAllWords(genre)}-Bücher | HermesReads`,
      author: (author = 'Unbekannt') => `${capitalizeAllWords(stripHtml(author))} | Autor auf HermesReads`,
      search: (query = 'Ihre Suche') => `Suche ${quoteSmart(stripHtml(query))} | HermesReads`
    },
    description: {
      book: ({ title = 'Unbenannt', author = 'Unbekannt' }) => `Lies ${quoteSmart(capitalize(stripHtml(title)))} von ${capitalizeAllWords(stripHtml(author))} kostenlos auf HermesReads.`,
      genre: (genre = 'diesem Genre') => `Tauche in ${capitalizeAllWords(genre)}-Bücher ein, von Klassikern bis Philosophie.`,
      author: (author = 'diesem Autor') => `Entdecke kostenlose Werke von ${capitalizeAllWords(stripHtml(author))} auf HermesReads.`,
      search: (query = 'Ihre Suche') => `Finde ${quoteSmart(stripHtml(query))} im HermesReads-Archiv kostenloser Bücher.`
    },
    variants: {
      book: {
        concise: ({ title = 'Unbenannt', author = 'Unbekannt' }) => `Lies ${quoteSmart(capitalize(stripHtml(title)))} Kostenlos | HermesReads`,
        bold: ({ title = 'Unbenannt', author = 'Unbekannt' }) => `Entdecke ${quoteSmart(capitalize(stripHtml(title)))} von ${capitalizeAllWords(stripHtml(author))}! | HermesReads`,
        curiosity: ({ title = 'Unbenannt', author = 'Unbekannt' }) => `Warum ${quoteSmart(capitalize(stripHtml(title)))} von ${capitalizeAllWords(stripHtml(author))}? | HermesReads`
      },
      genre: {
        concise: (genre = 'diesem Genre') => `${capitalizeAllWords(genre)}-Bücher | HermesReads`,
        bold: (genre = 'diesem Genre') => `Finde ${capitalizeAllWords(genre)}-Klassiker! | HermesReads`,
        curiosity: (genre = 'diesem Genre') => `Was macht ${capitalizeAllWords(genre)} besonders? | HermesReads`
      },
      author: {
        concise: (author = 'Unbekannt') => `${capitalizeAllWords(stripHtml(author))}-Bücher | HermesReads`,
        bold: (author = 'Unbekannt') => `Entdecke ${capitalizeAllWords(stripHtml(author))}! | HermesReads`,
        curiosity: (author = 'Unbekannt') => `Wer war ${capitalizeAllWords(stripHtml(author))}? | HermesReads`
      },
      search: {
        concise: (query = 'Ihre Suche') => `Suche ${quoteSmart(stripHtml(query))} | HermesReads`,
        bold: (query = 'Ihre Suche') => `Finde ${quoteSmart(stripHtml(query))} Jetzt! | HermesReads`,
        curiosity: (query = 'Ihre Suche') => `Was steckt hinter ${quoteSmart(stripHtml(query))}? | HermesReads`
      }
    }
  },
  fr: {
    friendlyType: {
      book: 'Livre',
      genre: 'Genre',
      author: 'Auteur',
      search: 'Recherche'
    },
    title: {
      book: ({ title = 'Sans titre', author = 'Inconnu' }) => `${quoteSmart(capitalize(stripHtml(title)))} par ${capitalizeAllWords(stripHtml(author))} | HermesReads`,
      genre: (genre = 'ce genre') => `Explorer ${capitalizeAllWords(genre)} Livres | HermesReads`,
      author: (author = 'Inconnu') => `${capitalizeAllWords(stripHtml(author))} | Auteur sur HermesReads`,
      search: (query = 'votre recherche') => `Résultats pour ${quoteSmart(stripHtml(query))} | HermesReads`
    },
    description: {
      book: ({ title = 'Sans titre', author = 'Inconnu' }) => `Lisez ${quoteSmart(capitalize(stripHtml(title)))} par ${capitalizeAllWords(stripHtml(author))} gratuitement sur HermesReads.`,
      genre: (genre = 'ce genre') => `Plongez dans les livres ${capitalizeAllWords(genre)}, des classiques à la philosophie.`,
      author: (author = 'cet auteur') => `Découvrez les œuvres gratuites de ${capitalizeAllWords(stripHtml(author))} sur HermesReads.`,
      search: (query = 'votre recherche') => `Trouvez ${quoteSmart(stripHtml(query))} dans l'archive HermesReads.`
    },
    variants: {
      book: {
        concise: ({ title = 'Sans titre', author = 'Inconnu' }) => `Lisez ${quoteSmart(capitalize(stripHtml(title)))} Gratuit | HermesReads`,
        bold: ({ title = 'Sans titre', author = 'Inconnu' }) => `Découvrez ${quoteSmart(capitalize(stripHtml(title)))} par ${capitalizeAllWords(stripHtml(author))}! | HermesReads`,
        curiosity: ({ title = 'Sans titre', author = 'Inconnu' }) => `Pourquoi lire ${quoteSmart(capitalize(stripHtml(title)))}? | HermesReads`
      },
      genre: {
        concise: (genre = 'ce genre') => `Livres ${capitalizeAllWords(genre)} | HermesReads`,
        bold: (genre = 'ce genre') => `Explorez ${capitalizeAllWords(genre)} Classics! | HermesReads`,
        curiosity: (genre = 'ce genre') => `Qu'est-ce qui rend ${capitalizeAllWords(genre)} spécial? | HermesReads`
      },
      author: {
        concise: (author = 'Inconnu') => `Livres de ${capitalizeAllWords(stripHtml(author))} | HermesReads`,
        bold: (author = 'Inconnu') => `Découvrez ${capitalizeAllWords(stripHtml(author))} Maintenant! | HermesReads`,
        curiosity: (author = 'Inconnu') => `Qui était ${capitalizeAllWords(stripHtml(author))}? | HermesReads`
      },
      search: {
        concise: (query = 'votre recherche') => `Recherche ${quoteSmart(stripHtml(query))} | HermesReads`,
        bold: (query = 'votre recherche') => `Trouvez ${quoteSmart(stripHtml(query))} Maintenant! | HermesReads`,
        curiosity: (query = 'votre recherche') => `Que cache ${quoteSmart(stripHtml(query))}? | HermesReads`
      }
    }
  },
  sv: {
    friendlyType: {
      book: 'Bok',
      genre: 'Genre',
      author: 'Författare',
      search: 'Sök'
    },
    title: {
      book: ({ title = 'Namnlös', author = 'Okänd' }) => `${quoteSmart(capitalize(stripHtml(title)))} av ${capitalizeAllWords(stripHtml(author))} | HermesReads`,
      genre: (genre = 'denna genre') => `Utforska ${capitalizeAllWords(genre)}-böcker | HermesReads`,
      author: (author = 'Okänd') => `${capitalizeAllWords(stripHtml(author))} | Författare på HermesReads`,
      search: (query = 'din sökning') => `Sökresultat för ${quoteSmart(stripHtml(query))} | HermesReads`
    },
    description: {
      book: ({ title = 'Namnlös', author = 'Okänd' }) => `Läs ${quoteSmart(capitalize(stripHtml(title)))} av ${capitalizeAllWords(stripHtml(author))} gratis på HermesReads.`,
      genre: (genre = 'denna genre') => `Upptäck ${capitalizeAllWords(genre)}-böcker, från klassiker till filosofi.`,
      author: (author = 'denna författare') => `Utforska verk av ${capitalizeAllWords(stripHtml(author))} gratis på HermesReads.`,
      search: (query = 'din sökning') => `Hitta ${quoteSmart(stripHtml(query))} i HermesReads arkiv av gratis böcker.`
    },
    variants: {
      book: {
        concise: ({ title = 'Namnlös', author = 'Okänd' }) => `Läs ${quoteSmart(capitalize(stripHtml(title)))} Gratis | HermesReads`,
        bold: ({ title = 'Namnlös', author = 'Okänd' }) => `Upptäck ${quoteSmart(capitalize(stripHtml(title)))} av ${capitalizeAllWords(stripHtml(author))}! | HermesReads`,
        curiosity: ({ title = 'Namnlös', author = 'Okänd' }) => `Varför läsa ${quoteSmart(capitalize(stripHtml(title)))}? | HermesReads`
      },
      genre: {
        concise: (genre = 'denna genre') => `${capitalizeAllWords(genre)}-böcker | HermesReads`,
        bold: (genre = 'denna genre') => `Utforska ${capitalizeAllWords(genre)}-klassiker! | HermesReads`,
        curiosity: (genre = 'denna genre') => `Vad gör ${capitalizeAllWords(genre)} speciellt? | HermesReads`
      },
      author: {
        concise: (author = 'Okänd') => `${capitalizeAllWords(stripHtml(author))} Böcker | HermesReads`,
        bold: (author = 'Okänd') => `Upptäck ${capitalizeAllWords(stripHtml(author))} Nu! | HermesReads`,
        curiosity: (author = 'Okänd') => `Vem var ${capitalizeAllWords(stripHtml(author))}? | HermesReads`
      },
      search: {
        concise: (query = 'din sökning') => `Sök ${quoteSmart(stripHtml(query))} | HermesReads`,
        bold: (query = 'din sökning') => `Hitta ${quoteSmart(stripHtml(query))} Nu! | HermesReads`,
        curiosity: (query = 'din sökning') => `Vad döljer ${quoteSmart(stripHtml(query))}? | HermesReads`
      }
    }
  },
  es: {
    friendlyType: {
      book: 'Libro',
      genre: 'Género',
      author: 'Autor',
      search: 'Búsqueda'
    },
    title: {
      book: ({ title = 'Sin título', author = 'Desconocido' }) => `${quoteSmart(capitalize(stripHtml(title)))} por ${capitalizeAllWords(stripHtml(author))} | HermesReads`,
      genre: (genre = 'Género desconocido') => `Explora libros de ${capitalizeAllWords(genre)} | HermesReads`,
      author: (author = 'Desconocido') => `${capitalizeAllWords(stripHtml(author))} | Autor en HermesReads`,
      search: (query = 'tu búsqueda') => `Resultados para ${quoteSmart(stripHtml(query))} | HermesReads`
    },
    description: {
      book: ({ title = 'Sin título', author = 'Desconocido' }) => `Lee ${quoteSmart(capitalize(stripHtml(title)))} de ${capitalizeAllWords(stripHtml(author))} gratis en HermesReads.`,
      genre: (genre = 'este género') => `Sumérgete en libros de ${capitalizeAllWords(genre)}, desde clásicos hasta filosofía.`,
      author: (author = 'este autor') => `Descubre obras gratuitas de ${capitalizeAllWords(stripHtml(author))} en HermesReads.`,
      search: (query = 'tu búsqueda') => `Encuentra ${quoteSmart(stripHtml(query))} en el archivo de HermesReads.`
    },
    variants: {
      book: {
        concise: ({ title = 'Sin título', author = 'Desconocido' }) => `Lee ${quoteSmart(capitalize(stripHtml(title)))} Gratis | HermesReads`,
        bold: ({ title = 'Sin título', author = 'Desconocido' }) => `¡Descubre ${quoteSmart(capitalize(stripHtml(title)))} de ${capitalizeAllWords(stripHtml(author))}! | HermesReads`,
        curiosity: ({ title = 'Sin título', author = 'Desconocido' }) => `¿Por qué leer ${quoteSmart(capitalize(stripHtml(title)))}? | HermesReads`
      },
      genre: {
        concise: (genre = 'este género') => `Libros de ${capitalizeAllWords(genre)} | HermesReads`,
        bold: (genre = 'este género') => `¡Explora clásicos de ${capitalizeAllWords(genre)}! | HermesReads`,
        curiosity: (genre = 'este género') => `¿Qué hace especial a ${capitalizeAllWords(genre)}? | HermesReads`
      },
      author: {
        concise: (author = 'Desconocido') => `Libros de ${capitalizeAllWords(stripHtml(author))} | HermesReads`,
        bold: (author = 'Desconocido') => `¡Descubre ${capitalizeAllWords(stripHtml(author))} Ahora! | HermesReads`,
        curiosity: (author = 'Desconocido') => `¿Quién fue ${capitalizeAllWords(stripHtml(author))}? | HermesReads`
      },
      search: {
        concise: (query = 'tu búsqueda') => `Busca ${quoteSmart(stripHtml(query))} | HermesReads`,
        bold: (query = 'tu búsqueda') => `¡Encuentra ${quoteSmart(stripHtml(query))} Ahora! | HermesReads`,
        curiosity: (query = 'tu búsqueda') => `¿Qué hay detrás de ${quoteSmart(stripHtml(query))}? | HermesReads`
      }
    }
  },
  pt: {
    friendlyType: { book: 'Livro', genre: 'Gênero', author: 'Autor', search: 'Pesquisa' },
    title: {
      book: ({ title = 'Sem título', author = 'Desconhecido' }) => `${title} por ${author} | HermesReads`,
      genre: (genre = 'Gênero desconhecido') => `Explore livros de ${genre} | HermesReads`,
      author: (author = 'Desconhecido') => `${author} | Autor no HermesReads`,
      search: (query = 'sua pesquisa') => `Resultados para "${query}" | HermesReads`
    },
    description: {
      book: ({ title = 'Sem título', author = 'Desconhecido' }) => `Leia "${title}" de ${author} gratuitamente no HermesReads.`,
      genre: (genre = 'este gênero') => `Mergulhe em livros de ${genre}, de clássicos à filosofia.`,
      author: (author = 'este autor') => `Explore obras de ${author} grátis no HermesReads.`,
      search: (query = 'sua pesquisa') => `Encontre "${query}" no acervo do HermesReads.`
    },
    variants: {
      book: {
        concise: ({ title = 'Sem título' }) => `Leia "${title}" Grátis | HermesReads`,
        bold:    ({ title = 'Sem título', author = 'Desconhecido' }) => `Descubra "${title}" — ${author}! | HermesReads`,
        curiosity: ({ title = 'Sem título' }) => `Por que ler "${title}"? | HermesReads`
      },
      genre: {
        concise: (genre = 'este gênero') => `Livros de ${genre} | HermesReads`,
        bold:    (genre = 'este gênero') => `Encontre clássicos de ${genre}! | HermesReads`,
        curiosity: (genre = 'este gênero') => `O que torna ${genre} especial? | HermesReads`
      },
      author: {
        concise: (author = 'Desconhecido') => `Livros de ${author} | HermesReads`,
        bold:    (author = 'Desconhecido') => `Descubra ${author} agora! | HermesReads`,
        curiosity: (author = 'Desconhecido') => `Quem foi ${author}? | HermesReads`
      },
      search: {
        concise: (query = 'sua pesquisa') => `Buscar "${query}" | HermesReads`,
        bold:    (query = 'sua pesquisa') => `Encontre "${query}" agora! | HermesReads`,
        curiosity: (query = 'sua pesquisa') => `O que há em "${query}"? | HermesReads`
      }
    }
  },
  ru: {
    friendlyType: {
      book: 'Книга',
      genre: 'Жанр',
      author: 'Автор',
      search: 'Поиск'
    },
    title: {
      book: ({ title = 'Без названия', author = 'Неизвестен' }) => `${title} — ${author} | HermesReads`,
      genre: (genre = 'Неизвестный жанр') => `Книги в жанре ${genre} | HermesReads`,
      author: (author = 'Неизвестен') => `${author} | Автор на HermesReads`,
      search: (query = 'ваш запрос') => `Результаты поиска "${query}" | HermesReads`
    },
    description: {
      book: ({ title = 'Без названия', author = 'Неизвестен' }) => `Читайте "${title}" автора ${author} бесплатно на HermesReads.`,
      genre: (genre = 'этот жанр') => `Погрузитесь в книги ${genre}, от классики до философии.`,
      author: (author = 'этот автор') => `Исследуйте бесплатные произведения ${author} на HermesReads.`,
      search: (query = 'ваш запрос') => `Найдите "${query}" в архиве HermesReads.`
    },
    variants: {
      book: {
        concise: ({ title = 'Без названия' }) => `Читайте "${title}" бесплатно | HermesReads`,
        bold: ({ title = 'Без названия', author = 'Неизвестен' }) => `Откройте "${title}" — ${author}! | HermesReads`,
        curiosity: ({ title = 'Без названия' }) => `Почему читать "${title}"? | HermesReads`
      },
      genre: {
        concise: (genre = 'этот жанр') => `Книги ${genre} | HermesReads`,
        bold: (genre = 'этот жанр') => `Откройте классику ${genre}! | HermesReads`,
        curiosity: (genre = 'этот жанр') => `Что делает ${genre} особенным? | HermesReads`
      },
      author: {
        concise: (author = 'Неизвестен') => `Книги ${author} | HermesReads`,
        bold: (author = 'Неизвестен') => `Исследуйте ${author} сейчас! | HermesReads`,
        curiosity: (author = 'Неизвестен') => `Кто такой ${author}? | HermesReads`
      },
      search: {
        concise: (query = 'ваш запрос') => `Поиск "${query}" | HermesReads`,
        bold: (query = 'ваш запрос') => `Найдите "${query}" сейчас! | HermesReads`,
        curiosity: (query = 'ваш запрос') => `Что скрывается за "${query}"? | HermesReads`
      }
    }
  },
  zh: {
    friendlyType: {
      book: '书',
      genre: '类型',
      author: '作者',
      search: '搜索'
    },
    title: {
      book: ({ title = '无标题', author = '未知' }) => `${title} 作者：${author} | HermesReads`,
      genre: (genre = '未知类型') => `探索 ${genre} 书籍 | HermesReads`,
      author: (author = '未知') => `${author} | 作者在HermesReads`,
      search: (query = '您的搜索') => `搜索 "${query}" | HermesReads`
    },
    description: {
      book: ({ title = '无标题', author = '未知' }) => `在HermesReads免费阅读“${title}”作者${author}。`,
      genre: (genre = '此类型') => `深入${genre}书籍，从经典到哲学。`,
      author: (author = '此作者') => `在HermesReads探索${author}的免费作品。`,
      search: (query = '您的搜索') => `在HermesReads档案中查找“${query}”。`
    },
    variants: {
      book: {
        concise: ({ title = '无标题' }) => `免费阅读“${title}” | HermesReads`,
        bold: ({ title = '无标题', author = '未知' }) => `发现“${title}” — ${author}! | HermesReads`,
        curiosity: ({ title = '无标题' }) => `为什么阅读“${title}”？ | HermesReads`
      },
      genre: {
        concise: (genre = '此类型') => `${genre}书籍 | HermesReads`,
        bold: (genre = '此类型') => `发现${genre}经典！ | HermesReads`,
        curiosity: (genre = '此类型') => `什么使${genre}特别？ | HermesReads`
      },
      author: {
        concise: (author = '未知') => `${author}书籍 | HermesReads`,
        bold: (author = '未知') => `现在探索${author}！ | HermesReads`,
        curiosity: (author = '未知') => `${author}是谁？ | HermesReads`
      },
      search: {
        concise: (query = '您的搜索') => `搜索“${query}” | HermesReads`,
        bold: (query = '您的搜索') => `现在找到“${query}”！ | HermesReads`,
        curiosity: (query = '您的搜索') => `“${query}”背后是什么？ | HermesReads`
      }
    }
  },
  pl: {
    friendlyType: {
      book: 'Książka',
      genre: 'Gatunek',
      author: 'Autor',
      search: 'Wyszukiwanie'
    },
    title: {
      book: ({ title = 'Bez tytułu', author = 'Nieznany' }) => `${title} autorstwa ${author} | HermesReads`,
      genre: (genre = 'Nieznany gatunek') => `Poznaj książki ${genre} | HermesReads`,
      author: (author = 'Nieznany') => `${author} | Autor na HermesReads`,
      search: (query = 'Twoje wyszukiwanie') => `Wyniki wyszukiwania "${query}" | HermesReads`
    },
    description: {
      book: ({ title = 'Bez tytułu', author = 'Nieznany' }) => `Przeczytaj „${title}” autorstwa ${author} za darmo na HermesReads.`,
      genre: (genre = 'ten gatunek') => `Zanurz się w książkach ${genre}, od klasyki do filozofii.`,
      author: (author = 'ten autor') => `Odkrywaj darmowe dzieła ${author} na HermesReads.`,
      search: (query = 'twoje wyszukiwanie') => `Znajdź „${query}” w archiwum HermesReads.`
    },
    variants: {
      book: {
        concise: ({ title = 'Bez tytułu' }) => `Przeczytaj „${title}” za darmo | HermesReads`,
        bold: ({ title = 'Bez tytułu', author = 'Nieznany' }) => `Odkryj „${title}” — ${author}! | HermesReads`,
        curiosity: ({ title = 'Bez tytułu' }) => `Dlaczego czytać „${title}”? | HermesReads`
      },
      genre: {
        concise: (genre = 'ten gatunek') => `Książki ${genre} | HermesReads`,
        bold: (genre = 'ten gatunek') => `Odkryj klasyki ${genre}! | HermesReads`,
        curiosity: (genre = 'ten gatunek') => `Co czyni ${genre} specjalnym? | HermesReads`
      },
      author: {
        concise: (author = 'Nieznany') => `Książki ${author} | HermesReads`,
        bold: (author = 'Nieznany') => `Odkrywaj ${author} teraz! | HermesReads`,
        curiosity: (author = 'Nieznany') => `Kim był ${author}? | HermesReads`
      },
      search: {
        concise: (query = 'twoje wyszukiwanie') => `Wyszukaj „${query}” | HermesReads`,
        bold: (query = 'twoje wyszukiwanie') => `Znajdź „${query}” teraz! | HermesReads`,
        curiosity: (query = 'twoje wyszukiwanie') => `Co stoi za „${query}”? | HermesReads`
      }
    }
  },
  it: {
    friendlyType: {
      book: 'Libro',
      genre: 'Genere',
      author: 'Autore',
      search: 'Ricerca'
    },
    title: {
      book: ({ title = 'Senza titolo', author = 'Sconosciuto' }) => `${title} di ${author} | HermesReads`,
      genre: (genre = 'Genere sconosciuto') => `Esplora libri ${genre} | HermesReads`,
      author: (author = 'Sconosciuto') => `${author} | Autore su HermesReads`,
      search: (query = 'la tua ricerca') => `Risultati per "${query}" | HermesReads`
    },
    description: {
      book: ({ title = 'Senza titolo', author = 'Sconosciuto' }) => `Leggi "${title}" di ${author} gratuitamente su HermesReads.`,
      genre: (genre = 'questo genere') => `Immergiti nei libri ${genre}, dai classici alla filosofia.`,
      author: (author = 'questo autore') => `Esplora opere gratuite di ${author} su HermesReads.`,
      search: (query = 'la tua ricerca') => `Trova "${query}" nell'archivio HermesReads.`
    },
    variants: {
      book: {
        concise: ({ title = 'Senza titolo' }) => `Leggi "${title}" Gratuitamente | HermesReads`,
        bold: ({ title = 'Senza titolo', author = 'Sconosciuto' }) => `Scopri "${title}" — ${author}! | HermesReads`,
        curiosity: ({ title = 'Senza titolo' }) => `Perché leggere "${title}"? | HermesReads`
      },
      genre: {
        concise: (genre = 'questo genere') => `Libri ${genre} | HermesReads`,
        bold: (genre = 'questo genere') => `Scopri classici ${genre}! | HermesReads`,
        curiosity: (genre = 'questo genere') => `Cosa rende ${genre} speciale? | HermesReads`
      },
      author: {
        concise: (author = 'Sconosciuto') => `Libri di ${author} | HermesReads`,
        bold: (author = 'Sconosciuto') => `Esplora ${author} ora! | HermesReads`,
        curiosity: (author = 'Sconosciuto') => `Chi era ${author}? | HermesReads`
      },
      search: {
        concise: (query = 'la tua ricerca') => `Cerca "${query}" | HermesReads`,
        bold: (query = 'la tua ricerca') => `Trova "${query}" ora! | HermesReads`,
        curiosity: (query = 'la tua ricerca') => `Cosa c'è dietro "${query}"? | HermesReads`
      }
    }
  },
  da: {
    friendlyType: {
      book: 'Bog',
      genre: 'Genre',
      author: 'Forfatter',
      search: 'Søgning'
    },
    title: {
      book: ({ title = 'Uden titel', author = 'Ukendt' }) => `${title} af ${author} | HermesReads`,
      genre: (genre = 'Ukendt genre') => `Udforsk ${genre} bøger | HermesReads`,
      author: (author = 'Ukendt') => `${author} | Forfatter på HermesReads`,
      search: (query = 'din søgning') => `Søgeresultater for "${query}" | HermesReads`
    },
    description: {
      book: ({ title = 'Uden titel', author = 'Ukendt' }) => `Læs "${title}" af ${author} gratis på HermesReads.`,
      genre: (genre = 'denne genre') => `Dyk ned i ${genre}-bøger, fra klassikere til filosofi.`,
      author: (author = 'denne forfatter') => `Udforsk gratis værker af ${author} på HermesReads.`,
      search: (query = 'din søgning') => `Find "${query}" i HermesReads arkiv.`
    },
    variants: {
      book: {
        concise: ({ title = 'Uden titel' }) => `Læs "${title}" Gratis | HermesReads`,
        bold: ({ title = 'Uden titel', author = 'Ukendt' }) => `Opdag "${title}" — ${author}! | HermesReads`,
        curiosity: ({ title = 'Uden titel' }) => `Hvorfor læse "${title}"? | HermesReads`
      },
      genre: {
        concise: (genre = 'denne genre') => `${genre}-bøger | HermesReads`,
        bold: (genre = 'denne genre') => `Find ${genre}-klassikere! | HermesReads`,
        curiosity: (genre = 'denne genre') => `Hvad gør ${genre} speciel? | HermesReads`
      },
      author: {
        concise: (author = 'Ukendt') => `${author}-bøger | HermesReads`,
        bold: (author = 'Ukendt') => `Udforsk ${author} nu! | HermesReads`,
        curiosity: (author = 'Ukendt') => `Hvem var ${author}? | HermesReads`
      },
      search: {
        concise: (query = 'din søgning') => `Søg "${query}" | HermesReads`,
        bold: (query = 'din søgning') => `Find "${query}" nu! | HermesReads`,
        curiosity: (query = 'din søgning') => `Hvad er bag "${query}"? | HermesReads`
      }
    }
  },
  fi: {
	  
	friendlyType: {
		book: 'Kirja',
		genre: 'Genre',
		author: 'Kirjailija',
		search: 'Haku'
	},
	  title: {
		book: ({ title = 'Nimetön', author = 'Tuntematon' }) => `${title} by ${author} | HermesReads`,
		genre: (genre = 'Tuntematon genre') => `Tutustu ${genre}-kirjoihin | HermesReads`,
		author: (author = 'Tuntematon') => `${author} | HermesReadsin kirjailija`,
		search: (query = 'hakusi') => `Hakutulokset haulle "${query}" | HermesReads`
	  },
	  description: {
		book: ({ title = 'Nimetön', author = 'Tuntematon' }) => `Lue "${title}" (${author}) ilmaiseksi HermesReadsissä.`,
		genre: (genre = 'tämä genre') => `Tutustu ${genre}-kirjoihin HermesReadsissä.`,
		author: (author = 'tämä kirjailija') => `Tutustu ${author} teoksiin HermesReadsissä.`,
		search: (query = 'hakusi') => `Etsi "${query}" HermesReads-arkistosta.`
	  },
	  variants: {
		book: {
		  concise: ({ title, author }) => `Lue "${title}" | HermesReads`,
		  bold: ({ title, author }) => `Tutustu "${title}" — ${author}! | HermesReads`,
		  curiosity: ({ title, author }) => `Miksi lukea "${title}"? | HermesReads`
		},
		genre: {
		  concise: (genre) => `${genre}-kirjat | HermesReads`,
		  bold: (genre) => `Löydä ${genre}-klassikot! | HermesReads`,
		  curiosity: (genre) => `Mikä tekee ${genre}-kirjoista erityisiä? | HermesReads`
		},
		author: {
		  concise: (author) => `${author} | HermesReads`,
		  bold: (author) => `Tutustu ${author}-kirjoihin! | HermesReads`,
		  curiosity: (author) => `Kuka oli ${author}? | HermesReads`
		},
		search: {
		  concise: (query) => `Haku: "${query}" | HermesReads`,
		  bold: (query) => `Löydä "${query}" nyt! | HermesReads`,
		  curiosity: (query) => `Mitä löytyy haulla "${query}"? | HermesReads`
		}
	  }
	 }
};
Object.freeze(ogLocale);
Object.freeze(RTL_LANGS);
Object.keys(templates).forEach(k => Object.freeze(templates[k]));
Object.freeze(templates);

const fallbackLang = 'en';

const selectABVariant = (type, lang, data, variantType) => {
  if (!variantType) return null;
  const vt = String(variantType).toLowerCase(); // unknown variant → safe fallback via default title/description below
  const langTpl = templates[lang];
  const enTpl = templates.en;
  const titleFn =
    langTpl?.variants?.[type]?.[vt] ||
    enTpl?.variants?.[type]?.[vt];
  const descFn =
    langTpl?.description?.[type] ||
    enTpl?.description?.[type];
  return (titleFn && descFn) ? { title: titleFn(data), description: descFn(data) } : null;
};

export const getFriendlyType = (type) => templates[fallbackLang]?.friendlyType?.[type] || 'Page';

export const getStructuredDataBundle = (type, lang, data) => {
  const authorUrl = data.author ? `${BASE_URL}/author/${encodeURIComponent(slugify(data.author))}` : undefined;

  const baseSchema = {
    '@context': 'https://schema.org',
    '@type': type === 'book' ? 'Book' : type === 'author' ? 'Person' : 'CollectionPage',
    'inLanguage': lang,
    publisher: {
      '@type': 'Organization',
      name: 'HermesReads',
      url: `${BASE_URL}/`,
      logo: {
        '@type': 'ImageObject',
        url: `${BASE_URL}/images/logo.png`
      }
    }
  };

  if (type === 'book') {
    return {
      ...baseSchema,
      'name': capitalize(stripHtml(data.title || 'Untitled')),
      'author': data.author ? {
        '@type': 'Person', 
        'name': `${capitalizeAllWords(stripHtml(data.author))}`,
        '@id': authorUrl,
        'sameAs': [
          ...(Array.isArray(data.sameAs) ? data.sameAs : []),
          ...(data.wikipedia ? [data.wikipedia] : [])
        ].filter(Boolean),
        ...(data.language ? { knowsLanguage: [String(data.language)] } : {})
      } : null,
      'genre': Array.isArray(data.categories) ? data.categories.map(c => capitalizeAllWords(String(c))) : undefined,
      ...(data.isbn ? { isbn: String(data.isbn) } : {}),
      'description': getLocalizedDescription(type, lang, data),
      'isAccessibleForFree': true,
      'bookFormat': 'EBook',
      'encodingFormat': 'application/epub+zip',
      'offers': {
        '@type': 'Offer',
        'price': '0',
        'priceCurrency': DEFAULT_CURRENCY,
        'availability': 'https://schema.org/InStock',
        'url': buildFallbackUrl('book', data)
      },
      'image': data.cover ? absolutize(sanitizeUrl(data.cover)) : undefined,
      'isPartOf': [
        { '@type': 'WebSite', '@id': `${BASE_URL}/#website` },
        ...(data.series ? [{ '@type': 'BookSeries', 'name': String(data.series) }] : [])
      ],
      'potentialAction': {
        '@type': 'ReadAction',
        'target': `${BASE_URL}/book/${encodeURIComponent(data.titleSlug || '')}`
      }
    };
  } else if (type === 'author') {
    return {
      ...baseSchema,
      'name': `${capitalizeAllWords(stripHtml(data.author || 'Unknown'))}`,
      'description': getLocalizedDescription(type, lang, data),
      '@id': authorUrl,
      'url': authorUrl,
      'sameAs': [
        ...(Array.isArray(data.sameAs) ? data.sameAs : []),
        ...(data.wikipedia ? [data.wikipedia] : [])
      ].filter(Boolean),
      ...(data.language ? { knowsLanguage: [String(data.language)] } : {}),
      'isPartOf': { '@type': 'WebSite', '@id': `${BASE_URL}/#website` },
      'potentialAction': {
        '@type': 'ViewAction',
        'target': authorUrl
      }
    };
  } else if (type === 'genre') {
    const g = data.genre;
    const gName = typeof g === 'string' ? g : (g?.genre || g?.name || g?.label || 'Unknown Genre');
    return {
      ...baseSchema,
      'name': capitalizeAllWords(gName),
      'description': getLocalizedDescription(type, lang, data),
      'about': { '@type': 'Thing', 'name': capitalizeAllWords(gName) },
      'isPartOf': { '@type': 'WebSite', '@id': `${BASE_URL}/#website` }
    };
  } else {
    return {
      ...baseSchema,
      '@type': ['SearchResultsPage', 'CollectionPage'],
      'name': `Search: ${stripHtml(data.query || 'your search')}`,
      'description': getLocalizedDescription(type, lang, data),
      'isPartOf': { '@type': 'WebSite', '@id': `${BASE_URL}/#website` },
      'potentialAction': {
        '@type': 'SearchAction',
        'target': `${BASE_URL}/search?query={search_term_string}`,
        'query-input': 'required name=search_term_string'
      }
    };
  }
};

export const getLocalizedTitle = (type, lang, data) => {
  return templates[lang]?.title?.[type]?.(data) || templates[fallbackLang].title[type](data);
};

export const getLocalizedDescription = (type, lang, data) => {
  return templates[lang]?.description?.[type]?.(data) || templates[fallbackLang].description[type](data);
};

export const getPaginationLinks = (baseUrl, page, totalPages) => {
  const mk = (p) => p === 1 ? baseUrl : `${baseUrl}?page=${p}`;
  const links = {};
  if (page > 1) links.prev = mk(page - 1);
  if (page < totalPages) links.next = mk(page + 1);
  links.first = mk(1);
  links.last = mk(totalPages);
  return links;
};

export const getMetadataBundle = (type, lang = 'en', data = {}, options = {}) => {
  const base = String(lang || fallbackLang);
  const [l,r] = base.split('-');
  const htmlLang = r ? `${l.toLowerCase()}-${r.toUpperCase()}` : l.toLowerCase();
  const dir = RTL_LANGS.includes(l.toLowerCase()) ? 'rtl' : 'ltr';
  
  // Normalize incoming data once
  const normalizedData = {
    ...data,
    title: clean(data.title),
    author: clean(data.author),
    // handle object-ish genre shapes too
    genre: typeof data.genre === 'string'
      ? clean(data.genre)
      : clean(data?.genre?.genre || data?.genre?.name || data?.genre?.label || ''),
    // ensure categories are clean strings
    categories: Array.isArray(data.categories)
      ? data.categories.map(c => clean(typeof c === 'string' ? c : (c?.name || c?.label || c)))
      : []
  };

  const langKey = l.toLowerCase(); // use primary subtag for template lookups
  const abVariant = selectABVariant(type, langKey, normalizedData, options.variant);
  const title = abVariant?.title || getLocalizedTitle(type, langKey, normalizedData);
  const description = abVariant?.description || getLocalizedDescription(type, langKey, normalizedData);
  const keywords = generateDynamicKeywords(normalizedData, type, langKey);
  
  const safeTitle = clamp(title, TITLE_CLAMP);
  const safeDesc  = clamp(description, DESCRIPTION_CLAMP);
  
  const absImage = absolutize(sanitizeUrl(options.image)) || absolutize(PLACEHOLDER_IMAGE);

// Make canonical always absolute (safe polish)
  const absUrl = absolutize(sanitizeUrl(options.url)) || buildFallbackUrl(type, data);
  const canonical = absUrl;

  const hreflangAlternates = [
    { hrefLang: 'x-default', href: canonical }
  ];

  const ogLocaleAlternates = Object.keys(ogLocale)
    .filter(k => k !== l.toLowerCase())
    .map(k => ogLocale[k]);
  
  const updatedTime = options.updatedTime;
  const publishedTime = options.publishedTime;
  const robots = type === 'search' ? ROBOTS_NOINDEX : ROBOTS_INDEX;

  const bundle = {
    // Pages should emit <meta name="robots" content={bundle.robots}> from this field.
    htmlLang,
    dir,
    title: safeTitle,
    description: safeDesc,
    keywords,
    og: {
      title: safeTitle,
      description: safeDesc,
      type: type === 'book' ? 'book' : (type === 'author' ? 'profile' : 'website'),
      image: absImage,
      imageAlt: safeTitle,
      imageWidth: OG_IMAGE_WIDTH,
      imageHeight: OG_IMAGE_HEIGHT,
      url: absUrl,
      site_name: 'HermesReads',
      locale: ogLocale[l.toLowerCase()] || 'en_US',
      localeAlternate: ogLocaleAlternates,
      ...(updatedTime ? { updated_time: updatedTime } : {}),
      ...(publishedTime ? { published_time: publishedTime } : {})
    },
    twitter: {
      title: safeTitle,
      description: safeDesc,
      card: 'summary_large_image',
      image: absImage,
      imageAlt: safeTitle,
      site: TWITTER_HANDLE,
      creator: TWITTER_HANDLE
    },
    structuredData: getStructuredDataBundle(type, htmlLang, normalizedData),
    robots,
    canonical,
    hreflangAlternates,
    ogLocaleAlternates: ogLocaleAlternates,
    ...(Array.isArray(options.seeAlso) && options.seeAlso.length ? { ogSeeAlso: options.seeAlso } : {})
  };

  bundle.structuredData = {
    ...bundle.structuredData,
    '@id': absUrl,
    url: absUrl,
    mainEntityOfPage: { '@type': 'WebPage', '@id': absUrl },
    'keywords': keywords.split(',').map(s => s.trim()).filter(Boolean),
    ...(publishedTime ? { datePublished: publishedTime } : {}),
    ...(updatedTime ? { dateModified: updatedTime } : {})
  };

  // Optional BreadcrumbList node
  const crumbs = [{ name: 'Home', item: BASE_URL }];
  if (type === 'genre') crumbs.push({ name: 'Genre', item: `${BASE_URL}/genre` });
  if (type === 'author') crumbs.push({ name: 'Author', item: `${BASE_URL}/author` });
  if (type === 'book' && data.genre) {
    const genreName = typeof data.genre === 'string'
      ? data.genre
      : (data.genre?.genre || data.genre?.name || data.genre?.label || '');
    crumbs.push({
      name: 'Genre',
      item: `${BASE_URL}/genre/${encodeURIComponent(slugify(genreName))}`
    });
  }
  const crumbTail = (type === 'book' && data.title) ? data.title
                     : (type === 'author' && data.author) ? data.author
                     : (type === 'genre' && data.genre) ? String(data.genre)
                     : null;
  if (crumbTail) crumbs.push({ name: crumbTail, item: absUrl });
  bundle.breadcrumbList = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((c,i) => ({ '@type': 'ListItem', position: i+1, name: c.name, item: c.item }))
  };

  // Optionally add an image if you have one (helps Books/Authors)
  if (!bundle.structuredData.image && absImage) {
    bundle.structuredData.image = absImage;
  }

  return bundle;
};